import { ApiError, can } from '@batiflow/client-core';
import { useSession } from '@batiflow/client-core/react';
import {
  EmptyState,
  ExternalSpaceBanner,
  OrgAvatar,
  SkeletonList,
  StatusChip,
  TabNav,
  isolate,
} from '@batiflow/ui';
import {
  Button,
  MessageBar,
  MessageBarActions,
  MessageBarBody,
  MessageBarTitle,
  __css,
  tokens,
} from '@fluentui/react-components';
import {
  Document24Regular,
  LockClosed16Regular,
  PeopleCommunity24Regular,
} from '@fluentui/react-icons';
import { Navigate, useNavigate } from '@tanstack/react-router';
import { Suspense, lazy } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocale } from '@/app/providers/i18n-provider';
import { SpaceMeetingsPanel, StartMeetingButton } from '@/features/meetings';
import { useRealtimeRoom } from '@/features/realtime/context';
import { useReaderOrgId, useSpace } from '../api';
import {
  accessParts,
  isClosed,
  isSpaceTab,
  visibleTabs,
  type Space,
  type SpaceTab,
} from '../model';
import { SpaceFrame } from '../space-frame';
import { SpaceSettingsTab } from '../space-settings-tab';
import { SpaceAuditTab } from '../sharing/audit-tab';
import { SpaceDelegatesTab } from '../sharing/delegates-tab';
import { GuestExpiryNotice } from '../sharing/guest-expiry-notice';
import { SpacePartnersTab } from '../sharing/partners-tab';
import { SpacePeopleTab } from '../sharing/people-tab';

// The documents workspace (folder tree, uploads, document panel) and the chat are their own
// chunks, loaded when their tab is opened.
const SpaceDocumentsTab = lazy(() =>
  import('../space-documents-tab').then((module) => ({ default: module.SpaceDocumentsTab })),
);
const SpaceCalendar = lazy(() =>
  import('@/features/calendar/space-calendar').then((module) => ({
    default: module.SpaceCalendar,
  })),
);
const SpaceSynthesisTab = lazy(() =>
  import('../synthesis/synthesis-tab').then((module) => ({ default: module.SpaceSynthesisTab })),
);
const SpaceGroupementTab = lazy(() =>
  import('../groupement/groupement-tab').then((module) => ({
    default: module.SpaceGroupementTab,
  })),
);
const SpaceChat = lazy(() =>
  import('@/features/chat').then((module) => ({ default: module.SpaceChat })),
);

const useStyles = __css({"summary":{"mc9l5x":"f22iagw","Eh141a":"fni485r","Bt984gj":"f122n59","i8kkvl":"f1xcgk68","Belr9w4":"f1wn3y0l","Dbcxam":0,"rjzwhg":0,"Bblux5w":"f9b8bpc","Bnt446c":0,"B1hrpyx":0,"Fjva8n":"fjshy37","De3pzq":"f1dmdbja"},"host":{"mc9l5x":"ftuwxu6","Bt984gj":"f122n59","i8kkvl":"fsnqrgy","Be2twd7":"fkhj508","Btd35i7":"fjksvth"},"access":{"mc9l5x":"f22iagw","Eh141a":"fni485r","i8kkvl":"f1xcgk68","jrapky":0,"Frg6f3":0,"t21cq0":0,"B6of3ja":0,"B74szlk":"f1s184ao","Byoj8tv":0,"uwmqm3":0,"z189sj":0,"z8tnut":0,"B0ocmuz":"f1mk8lai","dclx09":"ftrb29c","Be2twd7":"fy9rknc","sj55zd":"fkfq4zb"},"closedText":{"Be2twd7":"fy9rknc","sj55zd":"fkfq4zb"}});

/** Host avatar, host name, kind, closed state and (for an external space) how the reader got in. */
function SpaceSummary({ space }: { space: Space }) {
  const { t } = useTranslation('spaces');
  const styles = useStyles();
  const locale = useLocale();
  const { host } = space;
  return (
    <>
      {/* The persistent notice of another organisation's space (13-ui-ux.md §1). */}
      {space.access.external ? (
        <ExternalSpaceBanner hostName={host.name} hostLogoUrl={host.logoUrl} />
      ) : null}
      <section className={styles.summary} aria-label={t('page.summaryLabel')}>
        <span className={styles.host}>
          <OrgAvatar
            name={host.name}
            size={28}
            decorative
            {...(host.logoUrl ? { logoUrl: host.logoUrl } : {})}
          />
          <span>{t('page.hostedBy', { host: isolate(host.name) })}</span>
        </span>
        <StatusChip label={t(`kinds.${space.kind}`)} intent="neutral" />
        {isClosed(space) ? (
          <>
            <StatusChip
              label={t('status.closed')}
              intent="warning"
              icon={<LockClosed16Regular />}
            />
            <span className={styles.closedText}>{t('page.closedNotice')}</span>
          </>
        ) : null}
        {space.access.external ? (
          <ul className={styles.access}>
            {accessParts(space.access, t, locale).map((part) => (
              <li key={part}>{part}</li>
            ))}
          </ul>
        ) : null}
      </section>
      {/* A guest's access always ends: say when (a warning in the last 14 days). */}
      {space.access.via === 'guest' && space.access.expiresAt ? (
        <GuestExpiryNotice expiresAt={space.access.expiresAt} />
      ) : null}
    </>
  );
}

export interface SpacePageProps {
  spaceId: string;
  /** The open tab, from the address; absent or not allowed: the first tab the space offers. */
  tab?: string | undefined;
  onTabChange: (tab: SpaceTab) => void;
  /** Channel and message of the Chat tab, from the address. */
  channelId?: string | undefined;
  messageId?: string | undefined;
  onChannelChange: (channelId: string) => void;
}

/**
 * A shared or external space (12-frontend.md §2): the title bar with the host organisation, the
 * persistent banner when the space belongs to another organisation, and only the tabs the server
 * lists for the reader (documents, chat, settings today; the others say they arrive soon).
 */
export function SpacePage({
  spaceId,
  tab,
  onTabChange,
  channelId,
  messageId,
  onChannelChange,
}: SpacePageProps) {
  const { t } = useTranslation('spaces');
  const navigate = useNavigate();
  const space = useSpace(spaceId);
  const readerOrgId = useReaderOrgId(space.data);
  // Closing the space is for the owners and administrators of the host (cosmetic: the server decides).
  const hostPermissions = useSession(
    (state) => state.me?.memberships.find((m) => m.organizationId === readerOrgId)?.permissions,
  );
  const canClose = readerOrgId !== null && can(hostPermissions, 'org.policy.update');
  // Live updates of the space (documents, channels) while the screen is open.
  useRealtimeRoom(`space:${spaceId}`, { spaceId, fragments: [`/spaces/${spaceId}`] });

  const backToList = () => {
    void navigate({ to: '/spaces' });
  };
  const breadcrumb = (name: string) => [
    { label: t('title'), onSelect: backToList },
    { label: name },
  ];

  if (space.isError && !space.data) {
    // 403 and 404 look the same on purpose: nothing says whether the space exists.
    const gone =
      space.error instanceof ApiError && (space.error.status === 404 || space.error.status === 403);
    return (
      <SpaceFrame title={t('title')} breadcrumb={breadcrumb(t('title'))}>
        {gone ? (
          <EmptyState
            illustration={<PeopleCommunity24Regular />}
            title={t('page.notFoundTitle')}
            description={t('page.notFoundBody')}
            primaryAction={{ label: t('page.backToList'), onClick: backToList }}
          />
        ) : (
          <MessageBar intent="error" layout="multiline">
            <MessageBarBody>
              <MessageBarTitle>{t('page.loadFailedTitle')}</MessageBarTitle>
              {t('page.loadFailedBody')}
            </MessageBarBody>
            <MessageBarActions>
              <Button
                onClick={() => {
                  void space.refetch();
                }}
              >
                {t('common:actions.retry')}
              </Button>
            </MessageBarActions>
          </MessageBar>
        )}
      </SpaceFrame>
    );
  }

  if (!space.data) {
    return (
      <SpaceFrame title={t('title')} breadcrumb={breadcrumb(t('title'))}>
        <SkeletonList count={6} />
      </SpaceFrame>
    );
  }

  const data = space.data;
  const tabs = visibleTabs(data);
  const first = tabs[0];
  const open = tab !== undefined && isSpaceTab(tab) && tabs.includes(tab) ? tab : undefined;

  // The bare address, or a tab the reader may not open, leads to the first tab they may.
  if (open === undefined && first !== undefined) {
    return <Navigate to="/s/$spaceId/$tab" params={{ spaceId, tab: first }} replace />;
  }

  return (
    <SpaceFrame
      title={data.name}
      breadcrumb={breadcrumb(data.name)}
      banner={<SpaceSummary space={data} />}
      {...(open !== undefined
        ? {
            tabs: (
              <TabNav
                tabs={tabs.map((value) => ({ value, label: t(`tabs.${value}`) }))}
                selectedValue={open}
                onSelect={(value) => {
                  if (isSpaceTab(value)) onTabChange(value);
                }}
              />
            ),
          }
        : {})}
    >
      {open === undefined ? (
        <EmptyState
          illustration={<Document24Regular />}
          title={t('page.noTabsTitle')}
          description={t('page.noTabsBody')}
        />
      ) : open === 'documents' ? (
        <Suspense fallback={<SkeletonList count={6} />}>
          <SpaceDocumentsTab space={data} readerOrgId={readerOrgId} />
        </Suspense>
      ) : open === 'synthesis' ? (
        <Suspense fallback={<SkeletonList count={6} />}>
          <SpaceSynthesisTab spaceId={data.id} />
        </Suspense>
      ) : open === 'groupement' ? (
        <Suspense fallback={<SkeletonList count={6} />}>
          <SpaceGroupementTab space={data} orgId={readerOrgId} />
        </Suspense>
      ) : open === 'chat' ? (
        <Suspense fallback={<SkeletonList count={6} />}>
          <SpaceChat
            spaceId={data.id}
            orgId={readerOrgId}
            channelId={channelId}
            messageId={messageId}
            onChannelChange={onChannelChange}
          />
        </Suspense>
      ) : open === 'settings' ? (
        <SpaceSettingsTab space={data} canClose={canClose} />
      ) : open === 'partners' ? (
        <SpacePartnersTab space={data} hostOrgId={readerOrgId} />
      ) : open === 'people' ? (
        <SpacePeopleTab space={data} />
      ) : open === 'delegates' ? (
        <SpaceDelegatesTab space={data} />
      ) : open === 'audit' ? (
        <SpaceAuditTab space={data} />
      ) : open === 'calendar' ? (
        <Suspense fallback={<SkeletonList count={6} />}>
          <SpaceCalendar spaceId={data.id} />
        </Suspense>
      ) : (
        // Meetings of the space: the host is named for an external reader, joining is prominent.
        <SpaceMeetingsPanel
          spaceId={data.id}
          {...(data.access.external
            ? { intro: t('meetings.externalIntro', { host: isolate(data.host.name) }) }
            : {})}
          action={
            data._permissions.includes('can_contribute') ? (
              <StartMeetingButton
                orgId={null}
                spaceId={data.id}
                projectName={data.name}
                label="new"
              />
            ) : null
          }
        />
      )}
    </SpaceFrame>
  );
}


import "/opt/batiflow/app/apps/web/src/features/spaces/pages/space-page.griffel.css";